<template>
    <div v-if="selectedTemplate" class="onboarding-template-preview-container">
        <OnboardingTopNav :view-mode="viewMode" @changeViewMode="changeViewMode" @setPortfolioTheme="setPortfolioTheme" @goBack="goBack" />
        <div class="body-container">
            <div class="customization-actions-container">
                <div class="text-container">
                    <div class="title">Make it yours</div>
                    <div class="subtitle">Stand out by adding personal touch</div>
                </div>
                <div class="customization-container">
                    <div class="card-wrapper">
                        <div class="title">Colors</div>
                        <div class="card-grid">
                            <OnboardingBoxSelect
                                :selected="(selectedColorPalette && (selectedColorPalette.id === paletteOption.id))"
                                @handle-click="selectOption(paletteOption)"
                                v-for="paletteOption in templateAdditionalColorPalettes"
                                :key="paletteOption.id"
                                :type="'color'"
                            >
                                <template #content>
                                    <div class="color-preview-container">
                                        <div class="color-preview" :style="{backgroundColor: `${paletteOption.colors[0]}`}"></div>
                                        <div class="color-preview" :style="{backgroundColor: `${paletteOption.colors[2]}`}"></div>
                                        <div class="color-preview" :style="{backgroundColor: `${paletteOption.colors[4]}`}"></div>
                                    </div>
                                </template>
                            </OnboardingBoxSelect>
                        </div>
                    </div>
                    <div class="card-wrapper">
                        <div class="title">Fonts</div>
                        <div class="card-grid">
                            <OnboardingBoxSelect
                                v-bind:key="font.text"
                                :selected="(selectedFontPair && (selectedFontPair._id === font._id))"
                                :style="fontPairStyle(font)"
                                @handle-click="changeFontPair(font)"
                                v-for="font in templateAdditionalFontPairs"
                                :type="'font'"
                            >
                                <template #content>
                                    <div class="heading-style">Headings</div>
                                    <div class="paragraph-style">Body text</div>
                                </template>
                            </OnboardingBoxSelect>
                        </div>
                    </div>
                </div>
                <div class="banner-container">
                    <img alt="onboarding-illu" src="../../../images/illustrations/onboarding-customize-illustration.svg" />
                    <div class="text">Not your perfect combo? Try more styles in the editor.</div>
                </div>
            </div>

            <div class="preview-container" :class="[viewMode]">
                <FakeBrowser :width="viewMode === 'mobile' ? '390px' : null">
                    <template #content>
                        <div class="iframe-scale-wrapper" :class="viewMode">
                            <iframe
                                ref="previewFrame"
                                class="preview-frame"
                                :src="templateUrl + '?templatePreview=true'"
                            />
                        </div>
                    </template>
                </FakeBrowser>
            </div>
        </div>
    </div>
</template>

<script>

import {event} from "vue-gtag";
import OnboardingTopNav from "@/scripts/portfolioManager/onboarding/OnboardingTopNav.vue";
import FakeBrowser from "@/scripts/portfolioManager/onboarding/FakeBrowser.vue";
import {fetchColorPalettes} from "@/scripts/common/templateDefaults/colorPaletteCatalog.js";
import {mapActions, mapGetters} from "vuex";
import {preloadFontPair, preloadFontPairs} from "@/scripts/settingsEditor/_helpers/fontPreload.js";
import OnboardingBoxSelect from "@/scripts/portfolioManager/onboarding/components/OnboardingBoxSelect.vue";
import MixpanelService from "@/scripts/analytics/mixpanel.js";
import ApiHelper from "@/scripts/api/ApiHelper.js";

export default {
    name: 'OnboardingTemplatePreview',
    emits: ['set-portfolio-theme', 'create-new-portfolio'],
    components: {
        OnboardingBoxSelect,
        FakeBrowser,
        OnboardingTopNav,
    },
    data() {
        return {
            viewMode: 'desktop',
            allColorPalettes: [],
            availableFontPairs: [],
            selectedColorPalette:null,
            selectedFontPair:null,
            updatedStyles: {
                fontStyles:null,
                colorStyles:null
            }
        }
    },
    watch: {
        selectedTemplate: {
            handler(newValue) {
                if (!newValue?.defaults) return
                this.selectedColorPalette = this.getPalette(newValue.defaults.availableColorPalettes?.[0]) || null
                this.selectedFontPair = newValue.defaults.availableFontPairs?.[0] || null
            },
            immediate: false
        }
    },
    beforeMount() {
        if(!this.selectedTemplate) {
            this.$router.push('/home/onboarding');
        }
        window.scrollTo({top: 0});
    },
    async mounted() {
        if (window.scrollY) {window.scroll(0, 0);}
        this.$actions.trackPageView(`/home${this.$route.fullPath}`, `${this.isOnboardingFlow ? 'Create portfolio' : 'Onboarding'} - Preview selected theme`);
        this.$store.state.previewAndCreatePortfolioDetails.activeView = 2;

        await this.ensureSelectedTemplateDetails();
        await this.loadPalettes();
        this.selectedColorPalette = this.getPalette(this.selectedTemplate?.defaults?.availableColorPalettes?.[0]) || null
        this.selectedFontPair = this.selectedTemplate?.defaults?.availableFontPairs?.[0] || null
        preloadFontPair(this.selectedFontPair);
        preloadFontPairs(this.templateAdditionalFontPairs);

        //Watch for style changes to catch the new fonts and colors
        window.addEventListener("message", (event) => {
            if (event.data?.type === "STORE_UPDATE") {
                this.updatedStyles = {
                    fontStyles: event.data.payload.fontStyles,
                    colorStyles: event.data.payload.colors,
                }
            }
        });
    },
    methods: {
        ...mapActions("design", [
            "setFontPairs"
        ]),
        async loadPalettes() {
            try {
                this.allColorPalettes = await fetchColorPalettes();
            } catch (e) {
                console.error("Failed to fetch color palettes", e);
                this.allColorPalettes = [];
            }
        },
        async ensureSelectedTemplateDetails() {
            if (!this.selectedTemplate?.portfolioShortId || this.selectedTemplate.defaults) return;

            const existingTemplate = this.$store.state.portfolioTemplates
                ?.find(template => template.portfolioShortId === this.selectedTemplate.portfolioShortId);

            if (existingTemplate?.defaults) {
                this.$store.state.previewAndCreatePortfolioDetails.selectedPortfolioTemplate = existingTemplate;
                return;
            }

            try {
                const { data } = await ApiHelper.get(`/api/portfolio-template/${this.selectedTemplate.portfolioShortId}`);
                if (!this.$store.state.portfolioTemplates) {
                    this.$store.state.portfolioTemplates = [];
                }

                const templateIndex = this.$store.state.portfolioTemplates
                    .findIndex(template => template.portfolioShortId === data.portfolioShortId);

                if (templateIndex >= 0) {
                    this.$store.state.portfolioTemplates.splice(templateIndex, 1, data);
                } else {
                    this.$store.state.portfolioTemplates.push(data);
                }

                this.$store.state.previewAndCreatePortfolioDetails.selectedPortfolioTemplate = data;
            } catch (e) {
                console.log('Error while fetching selected template details', e);
            }
        },
        selectOption(palette) {
            this.selectedColorPalette = palette;
            this.$refs.previewFrame.contentWindow.postMessage({
                type: "UPDATE_PALETTE",
                payload: {
                    ID: palette.id,
                    COLORS: JSON.parse(JSON.stringify(palette.colors))
                }
            }, "*")
        },
        changeFontPair(fontPair) {
            const plainFontPair = JSON.parse(JSON.stringify(fontPair));
            this.selectedFontPair = fontPair;
            preloadFontPair(fontPair);
            this.$refs.previewFrame.contentWindow.postMessage({ type: "UPDATE_FONT_PAIR", payload: plainFontPair }, "*")
        },
        setPortfolioTheme() {
            this.$store.state.previewAndCreatePortfolioDetails.selectedPortfolioTemplate = this.selectedTemplate;
            if(this.customizationApplied) {
                MixpanelService.track("TEMPLATE_CUSTOMIZATION_APPLIED",
                    {
                        ...MixpanelService.getUrls(),
                        "event_group": MixpanelService.MixpanelEventGroups.ONBOARDING,
                        "source": "Template preview",
                        "type": this.customizationType
                    }
                )
            }

            if (this.customizationApplied) {
                const fontPair = this.selectedFontPair;
                const colorPalette = {
                    id: this.selectedColorPalette.id,
                    colors: this.selectedColorPalette.colors
                };
                const styles = this.updatedStyles;

                this.$emit('create-new-portfolio', fontPair, colorPalette, styles);
            } else {
                this.$emit('create-new-portfolio');
            }
        },
        goBack() {
            this.$store.state.previewAndCreatePortfolioDetails.activeView = 1;
            this.$router.push('/home/onboarding').then(() => {

                event('Back to start: ' + this.selectedTemplate.name, {
                    'event_category': 'Onboarding',
                    'event_label':  'User: ' + this.$store.state.user._id
                });

                document.querySelector("body").classList.remove("thumbnail-generator-visible");
                this.$store.state.previewAndCreatePortfolioDetails.selectedPortfolioTemplate = null;
            });
        },
        changeViewMode(mode) {
            this.viewMode = mode;
        },
        fontPairStyle(font) {
            return {
                '--headingFont': font.headline,
                '--paragraphFont': font.text
            }
        },
        getPalette(simplePalette) {
            if (!simplePalette) return null;
            return this.allColorPalettes.find((palette) => palette.id === simplePalette);
        }
    },
    computed: {
        ...mapGetters("design", ["fontStyles"],),
        templateUrl() {
            return `${location.origin}/${this.selectedTemplate.portfolioShortId}`
        },
        selectedTemplate() {
            return this.$store.state.previewAndCreatePortfolioDetails.selectedPortfolioTemplate;
        },
        isOnboardingFlow() {
            return !this.$store.state.creatingNewPortfolio
        },
        templateAdditionalFontPairs() {
            const selectedTemplate = this.selectedTemplate;
            if (!selectedTemplate) {
                return [];
            }

            const template = this.$store.state.portfolioTemplates.find(template => template.portfolioShortId === selectedTemplate.portfolioShortId);
            return template?.defaults?.availableFontPairs || [];
        },
        templateAdditionalColorPalettes() {
            const selectedTemplate = this.selectedTemplate;
            if (!selectedTemplate) {
                return this.selectedColorPalette ? [this.selectedColorPalette] : [];
            }

            const template = this.$store.state.portfolioTemplates.find(template => template.portfolioShortId === selectedTemplate.portfolioShortId);
            const palettes = template?.defaults?.availableColorPalettes;

            if (!palettes?.length) {
                return this.selectedColorPalette ? [this.selectedColorPalette] : [];
            }

            return palettes.map((paletteId) => this.getPalette(paletteId)).filter(Boolean);
        },
        customizationApplied() {
            if (!this.selectedColorPalette || !this.selectedFontPair || !this.templateAdditionalColorPalettes[0] || !this.templateAdditionalFontPairs[0]) {
                return false;
            }
            return this.selectedColorPalette.id !== this.templateAdditionalColorPalettes[0].id || this.selectedFontPair._id !== this.templateAdditionalFontPairs[0]._id;
        },
        customizationType() {
            if (!this.selectedColorPalette || !this.selectedFontPair || !this.templateAdditionalColorPalettes[0] || !this.templateAdditionalFontPairs[0]) {
                return "None";
            }
            const colorChanged = this.selectedColorPalette.id !== this.templateAdditionalColorPalettes[0].id;
            const fontChanged = this.selectedFontPair._id !== this.templateAdditionalFontPairs[0]._id;

            if (colorChanged && fontChanged) return "Both";
            if (colorChanged) return "Colors";
            if (fontChanged) return "Fonts";
            return "None";
        },
    }
}
</script>

<style lang="scss" scoped>

.onboarding-template-preview-container {

    .body-container {
        display: flex;
        max-width: 2440px;
        margin-left: auto;

        .customization-actions-container {
            display: flex;
            width: 420px;
            height: 100%;
            padding: 30px 44px 30px 75px;
            flex-direction: column;
            align-items: flex-start;
            gap: 40px;
            border-right: 1px solid var(--asphalt20);


            .text-container {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;

                .title {
                    color: var(--asphalt100);
                    font: var(--editor-ui-h0);
                }

                .subtitle {
                    color: var(--asphalt50);
                    font: var(--editor-ui-b2-regular)
                }
            }

            .banner-container {
                flex-shrink: 0;
                align-self: stretch;
                padding: 20px 57px 20px 63px;
                border-radius: 8px;
                background: var(--asphalt15);
                position:relative;

                color: var(--asphalt60);
                font: var(--editor-ui-b2-regular);

                img {
                    position: absolute;
                    top: 0;
                    left: -24px;
                    filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.10)) drop-shadow(1px 2px 2px rgba(0, 0, 0, 0.09)) drop-shadow(2px 4px 3px rgba(0, 0, 0, 0.05)) drop-shadow(3px 7px 3px rgba(0, 0, 0, 0.01)) drop-shadow(5px 12px 4px rgba(0, 0, 0, 0.00));
                }
            }
        }

        .customization-container {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 24px;

            .card-wrapper {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 20px;

                .title {
                    color: var(--asphalt50);
                    font: var(--ai-ui-b2-regular);
                }

                .card-grid {
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    align-self: stretch;
                    flex-wrap: wrap;
                }

                .heading-style {
                    font-family: var(--headingFont);
                    font-size: 18px;
                    color: var(--asphalt100);
                    font-weight: 600;
                    line-height: normal;
                }
                .paragraph-style {
                    font-family: var(--paragraphFont);
                    font-size: 13px;
                    line-height: normal;
                    color: var(--asphalt50);
                }

                .color-preview-container {
                    display: flex;
                    height: 32px;
                    align-items: center;
                    flex: 1 0 0;
                    border-radius: 999px;
                    border: 1px solid rgba(0, 0, 0, 0.32);
                    box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.08);
                    overflow:hidden;

                    .color-preview {
                        width: 100%;
                        height: 100%;
                        background: var(--asphalt90);
                    }
                }
            }
        }

        .preview-container {
            display: flex;
            height: calc(100vh - 66px);
            padding: 60px 50px;
            flex-direction: column;
            gap: 20px;
            background: var(--asphalt15);
            flex: 1;
            align-items: center;

            .iframe-scale-wrapper {
                position: relative;
                width: 100%;
                height: 100%;
                overflow: hidden;

                .preview-frame {
                    transform: scale(0.8);
                    transform-origin: left top;
                    width: 125%;
                    height: 125%;
                    border: none;
                }
            }
        }
    }
}
</style>
